import { MAX_CHARACTER_STAGE, OBSERVER_STAGE } from '../content/progression';

export type RankingCategory = 'score' | 'time';

export type RankingSubmission = Readonly<{
  runId: string;
  score: number;
  stage: number;
  chain: number;
  elapsedMs: number;
  clearTimeMs: number | null;
}>;

export type RankingEntry = RankingSubmission & Readonly<{
  name: string;
  avatarId?: string;
  recordedAt: number;
  rank: number;
  isYou: boolean;
}>;

export type RankingBoard = Readonly<{
  category: RankingCategory;
  entries: readonly RankingEntry[];
  personal: RankingEntry | null;
  participants: number;
}>;

export function formatRunTime(ms: number | null, precise = true): string {
  if (ms === null) return '—';
  const centiseconds = Math.floor(Math.max(0, ms) / 10);
  const minutes = Math.floor(centiseconds / 6000);
  const seconds = String(Math.floor(centiseconds / 100) % 60).padStart(2, '0');
  return `${String(minutes).padStart(2, '0')}:${seconds}${precise ? `.${String(centiseconds % 100).padStart(2, '0')}` : ''}`;
}

export function normalizePlayerName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.normalize('NFKC').trim();
  if (Array.from(name).length < 1 || Array.from(name).length > 16 || /[\p{Cc}\p{Cf}<>]/u.test(name)) return null;
  return name;
}

export function isRankingSubmission(value: unknown): value is RankingSubmission {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const integer = (n: unknown, min: number, max: number): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n >= min && n <= max;
  return typeof v.runId === 'string' && /^[\w-]{1,80}$/.test(v.runId)
    && integer(v.score, 0, 2_000_000_000) && integer(v.stage, 1, MAX_CHARACTER_STAGE) && integer(v.chain, 0, 100_000)
    && integer(v.elapsedMs, 0, 7 * 86_400_000)
    && (v.clearTimeMs === null || (integer(v.clearTimeMs, 1, v.elapsedMs) && (v.stage as number) >= OBSERVER_STAGE));
}
